import { ChangeDetectorRef, Component, HostListener, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Assistance } from '../../../models/assistance'
import { AssistanceStatusesService } from '../../../services/domain/codelists/assistance-statuses.service'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { BehaviorSubject, Observable, of, Subject, switchMap } from 'rxjs'
import { AlertController, IonicModule, ModalController } from '@ionic/angular'
import { RecurrenceEditingMode } from '../../recurrence/recurrenceEditing'
import { AssistanceEditorComponent } from '../assistance-editor/assistance-editor.component'
import { FeatureFlags } from '../../../shared/featureFlags'
import { AssistanceEditorModalService } from './assistance-editor-modal.service'
import { UsersService } from '../../../services/domain/users.service'
import { AssistanceEditorCloseReason } from './AssistanceEditorCloseReason'
import { FormStatusType } from '../../../models/functional/formStatusType'
import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { EditorAssistance } from '../../../models/EditorAssistance'
import { moreThanOneDefined } from '../../../services/utils'
import { EditorAssistanceBundle } from './EditorAssistanceBundle'
import { AssistanceTemplate } from '../../../models/AssistanceTemplate'
import { debounceTime, distinctUntilChanged, map, shareReplay, tap } from 'rxjs/operators'
import { clamp } from '../../../shared/utils/numberUtils'
import { AssistanceEditorModalEditableHandler } from './AssistanceEditorModalEditableHandler'
import { PrimeNgToastService } from '../../../services/prime-ng-toast/prime-ng-toast.service'

export type CreateEditorAssistanceProps = {
  plannedFrom: Date
  plannedTo?: Date
  clientId?: number
}

@Component({
  selector: 'app-assistance-editor-modal',
  templateUrl: './assistance-editor-modal.component.html',
  styleUrls: ['./assistance-editor-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, AssistanceEditorComponent, IonicModule, FormsModule, RouterModule, ReactiveFormsModule],
})
export class AssistanceEditorModalComponent implements OnInit, OnDestroy {
  private statusService = inject(AssistanceStatusesService)
  private modalCtrl = inject(ModalController)
  private toaster = inject(PrimeNgToastService)
  protected assistanceEditorModalService = inject(AssistanceEditorModalService)
  private cdr = inject(ChangeDetectorRef)
  protected userService = inject(UsersService)
  private alertCtrl = inject(AlertController)

  @ViewChild('editor') editor: AssistanceEditorComponent // TODO: Only used for recurrence mode, ideally get rid of
  @HostListener('document:keydown.escape') onKeydownHandler() {
    this.tryClose()
  }
  @ViewChild('segmentButton') segmentButton: any // cant set IonSegment to access .el
  // These three inputs below are mutually exclusive
  @Input() urid: string // TODO: do we need URID class as on backend?
  @Input() createProps: CreateEditorAssistanceProps
  @Input() template: AssistanceTemplate

  @Input() recurrenceEditingMode: RecurrenceEditingMode // TODO: Only normal / split

  private TAG = 'AssistanceEditorModalComponent'
  protected assistanceBundle$: Observable<EditorAssistanceBundle>

  protected uridChanged$$: BehaviorSubject<string>
  private selectedBundleSegment$$ = new BehaviorSubject<number>(0)
  protected selectedBundleSegment$ = this.selectedBundleSegment$$.asObservable().pipe(
    tap((value) => {
      this.lastSelectedSegment = +(this.selectedBundleSegment ?? value)
      this.selectedBundleSegment = value.toString()
    }),
    shareReplay(1),
  )
  private bundleLength = 1
  protected editableStateHandler = new AssistanceEditorModalEditableHandler(this.statusService)

  initialized = false

  selectedBundleSegment: string
  private lastSelectedSegment: number

  BUNDLE_SEGMENT_ADD = 'add'
  bundleChildAssistancesEnabled = FeatureFlags.childAssistances
  private unsubscribe = new Subject<void>()
  console = console

  async ngOnInit() {
    console.log(`${this.TAG}#onInit`)
    this.uridChanged$$ = new BehaviorSubject<string>(this.urid)

    // Component assumed to be used as a ionic modal, meaning the inputs never change and are being supplied in ngOnInit
    this.assistanceBundle$ = this.uridChanged$$.asObservable().pipe(
      distinctUntilChanged(), // helps to avoid a couple re-processing identical URIDs, but it still runs 1 extra time
      map((urid) => (this.urid = urid)),
      switchMap((urid) => this.initializeAssistanceBundle()),
      tap((bundle) => {
        this.bundleLength = bundle.count
        console.log('Initialized bundle:', bundle)

        const selectedIndex = bundle.assistanceIndex
        this.selectedBundleSegment$$.next(clamp(selectedIndex, 0, this.bundleLength - 1))
      }),
      switchMap((bundle) => {
        return this.selectedBundleSegment$.pipe(
          map((segment) => {
            bundle.selectedAssistanceIndex = +segment
            return bundle
          }),
        )
      }),
      debounceTime(200),
    )
    this.cdr.detectChanges()
  }

  initializeAssistanceBundle(): Observable<EditorAssistanceBundle> {
    console.log('initializeAssistanceBundle', this.urid, this.createProps, this.template)
    if (moreThanOneDefined([this.urid, this.createProps, this.template])) {
      throw new Error(`${this.TAG}: Too many input variables`)
    }
    if (!this.urid && !this.createProps && !this.template) {
      throw new Error(`${this.TAG}: Insufficient data to load assistance`)
    }

    console.log(`${this.TAG}#initializeAssistanceBundle`, this.urid, this.createProps, this.template)

    if (this.urid) {
      return this.assistanceEditorModalService.getBundleByURID(this.urid, this.recurrenceEditingMode)
    } else if (this.createProps) {
      return of(new EditorAssistanceBundle(EditorAssistance.createFromProps(EditorAssistance, this.createProps)))
    } else if (this.template) {
      return this.statusService
        .getByNameWithCache(AssistanceStatusName.PLANNED)
        .pipe(
          map(
            (defaultStatus) =>
              new EditorAssistanceBundle(EditorAssistance.fromTemplate(this.template, defaultStatus.id)),
          ),
        )
    }
  }

  tryClose(reason: AssistanceEditorCloseReason = AssistanceEditorCloseReason.MANUAL_CLOSE) {
    console.log(this.TAG, 'tryClose', reason)
    // In setTimeout so any other callbacks have chance to run first
    setTimeout(async () => {
      switch (this.assistanceEditorModalService.formStatus.statusType) {
        case FormStatusType.SAVING:
          this.toaster.displayCustomToast('info', 'Okno je možné zavřít až po uložení všech úprav. Zkuste to znovu.')
          break
        case FormStatusType.VALID_UNSAVED_CHANGES:
          await this.closeWithConfirmation()
          break
        default:
          try {
            await this.closeModal(reason)
          } catch (e) {
            console.warn(e)
          }
      }
    })
  }

  segmentChanged(event: any, assistanceBundle: EditorAssistanceBundle) {
    console.log(this.TAG, 'segmentChanged', event, assistanceBundle)
    let selectedIndex = +event.detail.value
    if (event.detail.value === 'add') {
      selectedIndex = assistanceBundle.children.length
    }
    const assistances = [assistanceBundle.main, ...assistanceBundle.children]
    if (selectedIndex >= 0 && selectedIndex < assistances.length) {
      const selectedAssistance = assistances[selectedIndex]
      if (selectedAssistance && selectedAssistance.id.toString() !== this.urid) {
        this.trySwitchAssistance(selectedIndex, assistances, selectedAssistance)
      }
    } else {
      console.warn(`No assistance found for index: ${selectedIndex}`)
    }
  }

  trySwitchAssistance(segmentNumber: number, assistances?: EditorAssistance[], selectedAssistance?: EditorAssistance) {
    console.log(this.TAG, 'trySwitchAssistance', segmentNumber)

    setTimeout(async () => {
      if (this.assistanceEditorModalService.formStatus.statusType === FormStatusType.SAVING) {
        this.toaster.displayCustomToast(
          'info',
          'Asistenci je možné přepnout až po uložení všech úprav. Zkuste to znovu.',
        )
      } else if (this.assistanceEditorModalService.formStatus.statusType === FormStatusType.VALID_UNSAVED_CHANGES) {
        await this.changeToSegmentWithConfirmation(
          assistances,
          selectedAssistance,
          segmentNumber,
          'Neuložené změny',
          'Pozor, ve formuláři jsou neuložené změny. Fakt chceš přepnout na jinou asistenci bez uložení?',
        )
      } else if (
        this.assistanceEditorModalService.formStatus.statusType === FormStatusType.INVALID ||
        this.assistanceEditorModalService.formStatus.statusType === FormStatusType.ERROR_VALIDATION ||
        this.assistanceEditorModalService.formStatus.statusType === FormStatusType.ERROR_MISSING_FIELD
      ) {
        await this.changeToSegmentWithConfirmation(
          assistances,
          selectedAssistance,
          segmentNumber,
          'Formulář obsahuje chyby',
          'Pozor, ve formuláři jsou chyby. Fakt chceš přepnout na jinou asistenci bez upravení? Formulář nebude uložen.',
        )
      } else {
        this.urid = selectedAssistance.id.toString()
        this.switchAssistance(segmentNumber)
      }
    })
  }

  switchAssistance(segmentNumber: number) {
    this.changeToSegment(segmentNumber)
  }

  mainAssistanceDeleted() {
    console.log(this.TAG, 'assistanceDeleted()')
    this.tryClose(AssistanceEditorCloseReason.DELETED_MAIN_ASSISTANCE)
  }

  goToClient(id: number) {
    this.closeModal(AssistanceEditorCloseReason.GO_TO_CLIENT_PROFILE).then(() => {
      this.assistanceEditorModalService.goToClient(id)
    })
  }

  async bundleChildAssistanceAddBtnClicked(assistanceBundle: EditorAssistanceBundle, event: MouseEvent) {
    console.log(this.TAG, 'addAssistanceClicked', event)

    try {
      const newAssistanceData = await this.assistanceEditorModalService.createBundleChild(assistanceBundle.main)
      console.log('newAssistanceData', newAssistanceData)
      this.selectedBundleSegment$$.next(assistanceBundle.children.length)
      this.uridChanged$$.next(newAssistanceData.id.toString())
    } catch (error) {
      console.error('Failed to add assistance:', error)
    }
  }

  changeToPreviousSegment() {
    this.changeToSegment(Math.max(Number(this.selectedBundleSegment) - 1, 0))
  }
  changeToLastSegment() {
    this.changeToSegment(this.bundleLength - 1)
  }
  changeToSegment(num: number) {
    this.selectedBundleSegment$$.next(clamp(num, 0, this.bundleLength - 1))
  }

  isCreateMode(assistanceBundle: EditorAssistanceBundle): boolean {
    return !assistanceBundle.main.id && !assistanceBundle.main.isRecurrent()
  }

  ngOnDestroy(): void {
    console.log(this.TAG, 'onDestroy')
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  bundleChildAssistanceDeleted(assistanceBundle: EditorAssistanceBundle, assistanceId: number) {
    const idx = assistanceBundle.children.findIndex((a) => a.id === assistanceId)
    assistanceBundle.children.splice(idx, 1)
    this.changeToPreviousSegment()
  }

  recurrenceEditingModeChanged(mode: RecurrenceEditingMode) {
    this.recurrenceEditingMode = mode
    this.cdr.detectChanges()
  }

  private closeModal(reason: AssistanceEditorCloseReason) {
    return this.modalCtrl.dismiss({ reason })
  }

  private async closeWithConfirmation() {
    const alert = await this.alertCtrl.create({
      header: 'Neuložené změny',
      message: 'Pozor, ve formuláři jsou neuložené změny. Fakt ho chceš zavřít bez uložení?',
      buttons: [
        {
          text: 'To ne!!!',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Ano, zavřít, neuložit.',
          cssClass: 'primary',
          handler: async () => {
            // this.lockService.unlock(this.assistanceBundle) // TODO: Review if we can get rid of lockService
            await this.closeModal(AssistanceEditorCloseReason.MANUAL_CLOSE)
          },
        },
      ],
    })

    await alert.present()
  }
  private async changeToSegmentWithConfirmation(assistances, selectedAssistance, segmentNumber, header, message) {
    const alert = await this.alertCtrl.create({
      header: header,
      message: message,
      buttons: [
        {
          text: 'To ne!!!',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            window.setTimeout(() => {
              this.urid = assistances[+this.selectedBundleSegment].id.toString()
              this.onActiveTabButtonClick()
              this.selectedBundleSegment$$.next(+this.selectedBundleSegment)
              this.urid = assistances[+this.selectedBundleSegment].id.toString()
            })
          },
        },
        {
          text: 'Ano, neuložit.',
          cssClass: 'primary',
          handler: async () => {
            this.urid = selectedAssistance.id.toString()
            this.switchAssistance(segmentNumber)
            this.assistanceEditorModalService.formStatus.reset()
          },
        },
      ],
    })
    await alert.present()
  }

  //Make active tab in UI
  private onActiveTabButtonClick() {
    for (const element of this.segmentButton.el.children) {
      if (element.classList.contains(`bundle-navig-assistance-${this.selectedBundleSegment}`)) {
        element.click()
      }
    }
  }

  test(mainAssistance: Assistance) {
    console.log(mainAssistance)
  }

  onURIDChange(urid: string) {
    console.log(`${this.TAG}#onURIDChange to ${urid}`)
    this.uridChanged$$.next(urid)
  }

  isBundleNavigEnabled() {
    if ([FormStatusType.SAVING].includes(this.assistanceEditorModalService.formStatus.statusType)) {
      return false
    }
    return true
  }
}
